import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabase";

const DEFAULT_RELATIONSHIP_TYPES = [
  "Referral",
  "Colleague",
  "Assistant",
  "Employee",
  "Vendor",
  "Friends & Family",
];

interface InlineConnectionInputProps {
  contactName: string;
  relationshipType: string;
  onContactNameChange: (value: string) => void;
  onContactIdChange: (id: string) => void;
  onRelationshipTypeChange: (value: string) => void;
  workspaceId: string;
}

export function InlineConnectionInput({
  contactName,
  relationshipType,
  onContactNameChange,
  onContactIdChange,
  onRelationshipTypeChange,
  workspaceId,
}: InlineConnectionInputProps) {
  // Contact search state
  const [searchResults, setSearchResults] = useState<
    Array<{ id: string; first_name: string; last_name: string; company_name?: string }>
  >([]);
  const [showContactResults, setShowContactResults] = useState(false);

  // Relationship suggestions state
  const [relationshipSuggestions, setRelationshipSuggestions] = useState<string[]>([]);
  const [showRelSuggestions, setShowRelSuggestions] = useState(false);

  const skipSearchRef = useRef(false);

  // Debounced contact search
  useEffect(() => {
    if (skipSearchRef.current) {
      skipSearchRef.current = false;
      return;
    }
    if (!contactName.trim() || !workspaceId) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      const trimmed = contactName.trim();
      const parts = trimmed.split(/\s+/);
      let orFilter = `first_name.ilike.%${trimmed}%,last_name.ilike.%${trimmed}%,company_name.ilike.%${trimmed}%`;
      if (parts.length >= 2) {
        orFilter += `,and(first_name.ilike.%${parts[0]}%,last_name.ilike.%${parts.slice(1).join(' ')}%)`;
      }

      const { data } = await supabase
        .from("contacts")
        .select("id, first_name, last_name, company_name")
        .eq("workspace_id", workspaceId)
        .is("deleted_at", null)
        .or(orFilter)
        .limit(10);
      setSearchResults(data || []);
      setShowContactResults((data || []).length > 0);
    }, 300);
    return () => clearTimeout(timer);
  }, [contactName, workspaceId]);

  // Fetch relationship types on mount, merging defaults at the front so they
  // always appear first in the documented order.
  useEffect(() => {
    if (!workspaceId) {
      setRelationshipSuggestions([...DEFAULT_RELATIONSHIP_TYPES]);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from("connections")
        .select("relationship_type")
        .eq("workspace_id", workspaceId);
      const defaultsLower = new Set(DEFAULT_RELATIONSHIP_TYPES.map((t) => t.toLowerCase()));
      const extras = Array.from(
        new Set(
          (data || [])
            .map((r) => (r.relationship_type || "").trim())
            .filter((t) => t && !defaultsLower.has(t.toLowerCase()))
        )
      ).sort();
      setRelationshipSuggestions([...DEFAULT_RELATIONSHIP_TYPES, ...extras]);
    })();
  }, [workspaceId]);

  const displayName = (c: { first_name: string; last_name: string; company_name?: string }) => {
    const name = [c.first_name, c.last_name].filter(Boolean).join(" ");
    return name || c.company_name || "Unnamed";
  };

  const filteredRelTypes = relationshipSuggestions.filter(
    (t) => !relationshipType.trim() || t.toLowerCase().includes(relationshipType.toLowerCase())
  );

  return (
    <div className="flex flex-col gap-1.5">
      {/* Contact name search */}
      <div className="relative">
        <Input
          value={contactName}
          onChange={(e) => onContactNameChange(e.target.value)}
          onFocus={() => searchResults.length > 0 && setShowContactResults(true)}
          onBlur={() => setTimeout(() => setShowContactResults(false), 200)}
          placeholder="Search contact name..."
          className="h-7 text-xs"
          autoComplete="off"
        />
        {showContactResults && searchResults.length > 0 && (
          <div className="absolute z-50 w-full mt-1 border rounded-md bg-popover shadow-md max-h-[200px] overflow-y-auto">
            {searchResults.map((contact) => (
              <div
                key={contact.id}
                className="px-2 py-1.5 text-xs hover:bg-accent cursor-pointer"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  skipSearchRef.current = true;
                  onContactNameChange(displayName(contact));
                  onContactIdChange(contact.id);
                  setShowContactResults(false);
                  setSearchResults([]);
                }}
              >
                {displayName(contact)}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Relationship type with suggestions */}
      <div className="relative">
        <Input
          value={relationshipType}
          onChange={(e) => onRelationshipTypeChange(e.target.value)}
          onFocus={() => setShowRelSuggestions(true)}
          onBlur={() => setTimeout(() => setShowRelSuggestions(false), 200)}
          placeholder="Relationship type..."
          className="h-7 text-xs"
          autoComplete="off"
        />
        {showRelSuggestions && filteredRelTypes.length > 0 && (
          <div className="absolute z-50 w-full mt-1 border rounded-md bg-popover shadow-md max-h-[200px] overflow-y-auto">
            {filteredRelTypes.map((type) => (
              <div
                key={type}
                className="px-2 py-1.5 text-xs hover:bg-accent cursor-pointer"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onRelationshipTypeChange(type);
                  setShowRelSuggestions(false);
                }}
              >
                {type}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
